/**
 * @typedef IncludedMediaOrImage
 * @type {object}
 * @property {object} attributes
 * @property {string} id
 * @property {object} relationships
 * @property {string} type
 */

/**
 * @typedef ParsedImage
 * @type {object}
 * @property {string} mediaId
 * @property {string} alt
 * @property {number} width
 * @property {number} height
 * @property {string} url
 */

/**
 * @typedef ParsedLink
 * @type {object}
 * @property {string} title
 * @property {string} href
 */

/**
 * @typedef Slide
 * @type {object}
 * @property {string} id
 * @property {ParsedImage} image
 * @property {string} title
 * @property {string} body
 * @property {ParsedLink} link
 * @property {string} footer
 */

/**
 * Find an included object based on content type
 * @param {object} arg
 * @param {IncludedMediaOrImage[]} arg.included - array of media or image objects
 * @param {string} arg.contentType - Drupal content type
 * @returns {IncludedMediaOrImage[]}
 */
function getIncludesOfType({ included, contentType }) {
  return included.filter((item) => item.type === contentType);
}

/**
 * Handle Drupal internal links
 * @param {string} [''] url
 * @returns {string|undefined}
 */
function cleanInternalLink(url) {
  return url?.replace(/^internal:/i, '');
}

/**
 * Get and image url from an image content object by id
 * @param {object} arg
 * @param {IncludedMediaOrImage[]} arg.imageFiles - array of included image objects
 * @param {string} arg.id - content id
 */
function getImageUrl({ imageFiles, id }) {
  const image = imageFiles.find((imageFile) => imageFile.id === id);
  return image?.attributes?.uri?.url;
}

/**
 * compile all image objects from included content objects
 * @param {IncludedMediaOrImage[]} included
 * @returns {ParsedImage[]}
 */
function getImages(included) {
  const mediaImages = getIncludesOfType({ included, contentType: 'media--image' });
  const imageFiles = getIncludesOfType({ included, contentType: 'file--file' });

  return mediaImages.map((media) => ({
    mediaId: media.id,
    alt: media.relationships.field_media_image.data.meta.alt,
    width: media.relationships.field_media_image.data.meta.width,
    height: media.relationships.field_media_image.data.meta.height,
    url: getImageUrl({
      imageFiles,
      id: media.relationships.field_media_image.data.id,
    }),
  }));
}

/**
 * get a single parsed image by id
 * @param {ParsedImages[]} parsedImages
 * @param {string} id
 * @returns {ParsedImage|undefined}
 */
function getImage(parsedImages, id) {
  return parsedImages.find(({ mediaId, url }) => mediaId === id && !!url);
}

/**
 *
 * @param {object} json the response json from a call to fetch carousel slides
 * @returns {Slide[]}
 */
export function parseCarouselResponse(json) {
  const { data: slides, included } = json;
  const images = getImages(included);

  return slides.map((slide) => ({
    id: slide.id,
    image: getImage(images, slide.relationships.field_slide_image?.data?.id),
    title: slide.attributes.field_slide_title ?? null,
    body: slide.attributes.field_slide_body?.processed ?? null,
    link: {
      title: slide.attributes.field_slide_link?.title ?? null,
      href: cleanInternalLink(slide.attributes.field_slide_link?.uri) ?? null,
    },
    footer: slide.attributes.field_slide_footer ?? null,
  }))
    .filter(({ image }) => !!image);
}
